import { useEffect, useState } from "react";
import { Clock, Bell, Sparkles, Play, TrendingUp, Eye, Send, Layers } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, errMsg } from "@/lib/api";

export default function AutomationPanel() {
  const [a, setA] = useState(null);
  const load = () => http.get("/automation").then((r) => setA(r.data));
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setA((x) => ({ ...x, ...patch }));
    try { await http.put("/settings", patch); toast.success("Automazioni aggiornate"); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const runNow = async () => {
    try { await http.post("/automation/run-now"); toast.success("Generazione avviata per tutte le competizioni"); } catch (e) { toast.error(errMsg(e)); }
  };
  const [weekly, setWeekly] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const upgrade = async () => {
    setUpgrading(true);
    try { const r = await http.post("/automation/upgrade-markets"); toast.success(r.data.queued ? `${r.data.queued} pronostici in aggiornamento con i nuovi mercati` : "Tutti i pronostici hanno già i nuovi mercati"); } catch (e) { toast.error(errMsg(e)); setUpgrading(false); }
  };
  const preview = async () => { const r = await http.get("/automation/weekly-preview"); setWeekly(r.data.text); };
  const sendWeekly = async () => {
    try { const r = await http.post("/automation/weekly-send"); toast.success(`Riepilogo inviato a ${r.data.sent} iscritti`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!a) return <div className="panel h-40 scan" />;

  return (
    <div data-testid="automation-panel" className="panel p-6 space-y-5">
      <h3 className="font-display text-2xl font-bold uppercase">Automazioni</h3>
      <div data-testid="upgrade-markets-box" className={`rounded-lg border p-4 flex flex-wrap items-center justify-between gap-3 ${a.missing_markets ? "border-[var(--gold)]/60 bg-[var(--gold)]/10" : "border-slate-800"}`}>
        <div className="text-sm">
          <div className="font-semibold flex items-center gap-2"><Layers size={16} className="text-[var(--gold)]" /> Nuovi mercati (1° tempo, multigol, combo...)</div>
          <div data-testid="missing-markets-count" className="text-slate-400 text-xs mt-1">{a.missing_markets ? `${a.missing_markets} pronostici di partite da giocare non hanno ancora i nuovi mercati` : "Tutti i pronostici delle partite da giocare hanno i nuovi mercati ✓"}</div>
        </div>
        <button data-testid="upgrade-markets-btn" disabled={!a.missing_markets || upgrading} onClick={upgrade} className="btn btn-gold py-2">{upgrading ? "Aggiornamento avviato..." : "Aggiungi nuovi mercati"}</button>
      </div>
      {!a.enabled_here && <p data-testid="automation-preview-note" className="text-xs text-[var(--draw)]">Anteprima: le automazioni girano solo sul sito pubblicato.</p>}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold flex items-center gap-2"><Sparkles size={16} className="text-[var(--gold)]" /> Pronostici automatici</div>
          <p className="text-sm text-slate-400">Ogni giorno l'AI genera i pronostici della giornata di tutte le competizioni.</p>
        </div>
        <Switch data-testid="auto-predictions-switch" checked={a.auto_predictions} onCheckedChange={(v) => save({ auto_predictions: v })} />
      </div>
      <div className="flex items-center gap-3 text-sm">
        <Clock size={16} className="text-slate-400" /> Orario
        <select data-testid="auto-hour-select" value={a.auto_hour} onChange={(e) => save({ auto_hour: Number(e.target.value) })} className="bg-slate-950 border border-slate-800 rounded-md px-2 py-1 font-mono">
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
        </select>
        <span className="text-slate-500 text-xs">ora italiana</span>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold flex items-center gap-2"><Bell size={16} className="text-[var(--tg)]" /> Avviso risultati</div>
          <p className="text-sm text-slate-400">A fine partita gli iscritti del bot ricevono il risultato. Sul canale vengono pubblicati solo i pronostici vinti.</p>
        </div>
        <Switch data-testid="result-alerts-switch" checked={a.result_alerts} onCheckedChange={(v) => save({ result_alerts: v })} />
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold flex items-center gap-2"><TrendingUp size={16} className="text-[var(--win)]" /> Riepilogo del lunedì</div>
          <p className="text-sm text-slate-400">Ogni lunedì alle 10:00 gli iscritti ricevono la percentuale di pronostici azzeccati della settimana.</p>
          <div className="flex gap-2 mt-2">
            <button data-testid="weekly-preview-btn" onClick={preview} className="btn btn-ghost py-1 text-xs"><Eye size={14} /> Anteprima</button>
            <button data-testid="weekly-send-btn" onClick={sendWeekly} className="btn btn-ghost py-1 text-xs"><Send size={14} /> Invia ora</button>
          </div>
        </div>
        <Switch data-testid="weekly-summary-switch" checked={a.weekly_summary} onCheckedChange={(v) => save({ weekly_summary: v })} />
      </div>
      {weekly && <div data-testid="weekly-preview-box" className="rounded-lg bg-[#17212b] p-4 border border-slate-800"><div className="bg-[#2b5278] rounded-xl rounded-bl-none p-3 text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: weekly }} /></div>}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-800">
        <span data-testid="automation-last-run" className="text-xs font-mono text-slate-400">Ultima esecuzione: {a.last_auto_run || "mai"}{a.last_auto_count != null ? ` · ${a.last_auto_count} pronostici` : ""}</span>
        <button data-testid="automation-run-now-btn" onClick={runNow} className="btn btn-ghost py-1.5"><Play size={14} /> Genera ora</button>
      </div>
    </div>
  );
}
